/**
 * Where a person adds files: a dashed area they can drop onto, and a Choose files button that
 * opens the picker, which Enter and Space reach from the keyboard. Files that do not fit the rules
 * (`extensions`, `maxBytes`) never reach `onFiles`. They stay in the list with the reason on their
 * own row ("report.docx was not added. It is a .docx file. Use .pdf or .png.") and a Dismiss button. Files that do
 * fit go to `onFiles`, and the caller shows their progress by passing `rows`.
 *
 * A refusal is read out at once, in an alert that is on the page before it has anything to say, and
 * only what is added is read, so dismissing one says nothing. Two or more refused together are
 * summed up above it ("2 files were not added.") and focus moves to that line, so the person lands
 * on what went wrong and the rows below say which. The list of added
 * files is a polite live region, so a finished upload is read out without cutting in.
 */
import * as stylex from "@stylexjs/stylex";
import type { ChangeEvent, DragEvent } from "react";
import { useEffect, useRef, useState } from "react";
import "@lattice/tokens/breakpoints.stylex";
import { breakpoints } from "@lattice/tokens/breakpoints.stylex";
import "@lattice/tokens/color.stylex";
import { color } from "@lattice/tokens/color.stylex";
import "@lattice/tokens/space.stylex";
import { border, radius, space } from "@lattice/tokens/space.stylex";
import { Button } from "./Button.tsx";
import { focusRing } from "./focus.ts";
import { Text } from "./Text.tsx";
const styles = {
  summary: {
    kogj98: "x1ghz6dp",
    k8WAf4: "x1jyo4x1",
    $$css: true
  }
};
export interface FileRow {
  readonly id: string;
  readonly name: string;
  readonly bytes: number;
  readonly state: "queued" | "uploading" | "done" | "failed";
  /** Why it failed, or what is happening. */
  readonly message?: string;
}
export interface FileDropProps {
  /** Names the area: "Lab reports". */
  readonly label: string;
  /** Allowed file endings with the dot: [".pdf", ".png"]. */
  readonly extensions: readonly string[];
  readonly maxBytes: number;
  readonly multiple?: boolean;
  readonly onFiles: (files: readonly File[]) => void;
  /** The accepted files and how far each has got. */
  readonly rows?: readonly FileRow[];
  readonly onRemove?: (id: string) => void;
  readonly testId?: string;
}
interface Rejected {
  readonly key: number;
  readonly name: string;
  readonly reason: string;
}
const STATES = {
  queued: "Waiting",
  uploading: "Uploading",
  done: "Uploaded",
  failed: "Failed"
} as const;
export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(bytes < 10 * 1024 * 1024 ? 1 : 0)} MB`;
}
function joinOr(items: readonly string[]): string {
  return items.length < 2 ? items.join("") : `${items.slice(0, -1).join(", ")} or ${items.at(-1)}`;
}
function endingOf(name: string): string {
  const dot = name.lastIndexOf(".");
  return dot < 0 ? "" : name.slice(dot).toLowerCase();
}

/** Why a file is refused, or `null` when it is fine. */
export function refusalFor(file: File, extensions: readonly string[], maxBytes: number): string | null {
  const ending = endingOf(file.name);
  if (!extensions.includes(ending)) return `It is ${ending === "" ? "a file with no ending" : `a ${ending} file`}. Use ${joinOr(extensions)}.`;
  if (file.size === 0) return "It is empty.";
  if (file.size > maxBytes) return `It is ${formatBytes(file.size)}. The limit is ${formatBytes(maxBytes)}.`;
  return null;
}
export function FileDrop({
  label,
  extensions,
  maxBytes,
  multiple = true,
  onFiles,
  rows = [],
  onRemove,
  testId
}: FileDropProps) {
  const input = useRef<HTMLInputElement>(null);
  const zone = useRef<HTMLDivElement>(null);

  /* A removed row takes its button with it, so focus goes back to the one control that stays. */
  const refocus = () => zone.current?.querySelector("button")?.focus();
  const counter = useRef(0);
  const [over, setOver] = useState(false);
  const [rejected, setRejected] = useState<readonly Rejected[]>([]);
  const summary = useRef<HTMLParagraphElement>(null);
  const [summaryAsked, setSummaryAsked] = useState(0);

  /* Several refusals at once put focus on the line that counts them. */
  useEffect(() => {
    if (summaryAsked > 0) summary.current?.focus();
  }, [summaryAsked]);
  const take = (files: readonly File[]) => {
    const kept: File[] = [];
    const refused: Rejected[] = [];
    for (const [at, file] of files.entries()) {
      if (!multiple && at > 0) {
        refused.push({
          key: counter.current += 1,
          name: file.name,
          reason: "Add one file at a time."
        });
        continue;
      }
      const reason = refusalFor(file, extensions, maxBytes);
      if (reason === null) kept.push(file);else refused.push({
        key: counter.current += 1,
        name: file.name,
        reason
      });
    }
    setRejected(all => [...all, ...refused]);
    if (refused.length >= 2) setSummaryAsked(asked => asked + 1);
    if (kept.length > 0) onFiles(kept);
  };
  const onChange = (event: ChangeEvent<HTMLInputElement>) => {
    take([...(event.currentTarget.files ?? [])]);
    event.currentTarget.value = "";
  };
  const onDrop = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setOver(false);
    take([...event.dataTransfer.files]);
  };
  return <div data-testid={testId}>
      <div ref={zone} role="group" aria-label={label} onDragOver={event => {
      event.preventDefault();
      setOver(true);
    }}
    /* Entering the button inside fires a leave on the area, so only a leave that goes outside it counts. */ onDragLeave={event => {
      const into = event.relatedTarget;
      if (!(into instanceof Node) || !event.currentTarget.contains(into)) setOver(false);
    }} onDrop={onDrop} {...{
      0: {
        className: "x78zum5 xdt5ytf x6s0dn4 x626il7 xq24xx7 x2b8uid xdq7b4a x1y0btm7 xv9mh3h x1gzjth6 x14ba300"
      },
      1: {
        className: "x78zum5 xdt5ytf x6s0dn4 x626il7 xq24xx7 x2b8uid x1gzjth6 xbsl7fq x4lnxqg x1n5zh2c x1jct50n"
      }
    }[!!over << 0]}>
        <div className="x1lliihq x1jre7q">
          <Text>
            {over ? `Release to add ${multiple ? "the files" : "the file"}` : `Drop ${multiple ? "files" : "a file"} here, or`}
          </Text>
        </div>
        <Button corners="rounded" variant="secondary" onClick={() => input.current?.click()}>
          {multiple ? "Choose files" : "Choose a file"}
        </Button>
        <Text variant="label" tone="secondary">
          {joinOr(extensions)} files, up to {formatBytes(maxBytes)}
        </Text>
        <input ref={input} type="file" multiple={multiple} accept={extensions.join(",")} tabIndex={-1} aria-hidden="true" onChange={onChange} className="x1s85apg" />
      </div>
      {rejected.length < 2 ? null : <p ref={summary} tabIndex={-1} {...stylex.props(styles.summary, focusRing)}>
          <Text tone="danger">{rejected.length} files were not added.</Text>
        </p>}
      {/* On the page before it has anything to say. Only what is added is read out, so dismissing a
          row says nothing, and the count above is read by the focus that moves to it. */}
      <div role="alert" aria-atomic="false" aria-relevant="additions">
        {rejected.length === 0 ? null : <ul aria-label={`${label}, files not added`} className="x78zum5 xdt5ytf x1173whf x1ghz6dp x1717udv xe8uvvx">
            {rejected.map(one => <li key={`r${one.key}`} className="x78zum5 x1a02dak x6s0dn4 x1qughib x626il7 x1a91ssu xdq7b4a x1y0btm7 xv9mh3h x1fbwzx x40k5rp">
                <span className="xeuugli xehxkzz xj0a0fe">
                  <Text tone="danger">
                    {one.name} was not added. {one.reason}
                  </Text>
                </span>
                <Button corners="rounded" variant="tertiary" size="sm" ariaLabel={`Dismiss ${one.name}`} onClick={() => {
            setRejected(all => all.filter(x => x.key !== one.key));
            refocus();
          }}>
                  Dismiss
                </Button>
              </li>)}
          </ul>}
      </div>
      <ul aria-live="polite" aria-label={`${label}, added files`} className="x78zum5 xdt5ytf x1173whf x1ghz6dp x1717udv xe8uvvx">
        {rows.map(row => <li key={row.id} className="x78zum5 x1a02dak x6s0dn4 x1qughib x626il7 x1a91ssu xdq7b4a x1y0btm7 xv9mh3h x1fbwzx x40k5rp">
            <span className="xeuugli xehxkzz xj0a0fe">
              <Text>{row.name}</Text>
              <Text variant="caption" tone={row.state === "failed" ? "danger" : "secondary"}>
                {formatBytes(row.bytes)}. {STATES[row.state]}
                {row.message === undefined ? "" : `. ${row.message}`}
              </Text>
            </span>
            {onRemove === undefined ? null : <Button corners="rounded" variant="tertiary" size="sm" ariaLabel={`Remove ${row.name}`} onClick={() => {
          onRemove(row.id);
          refocus();
        }}>
                Remove
              </Button>}
          </li>)}
      </ul>
    </div>;
}